import React, { useState } from 'react';
import { Info, Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';
import { ScopeResponseLineItem, ScopeHelpfulAnnotation } from '../../types';
import { ConfidenceBadge } from '../ScopeReviewComponents';
import { ProvenanceBadge } from '../ProvenanceBadge';
import { ScopeDebugDrawer } from './ScopeDebugDrawer';

// ================================================================= //
//          Source Color Configuration                                 //
// ================================================================= //

/**
 * Color coding for line item sources.
 * Each source type gets a distinctive left border and subtle background
 * so estimators can quickly see where each item came from.
 */
const SOURCE_STYLES: Record<string, { border: string; bg: string; label: string }> = {
  user_input: { border: 'border-l-gray-200', bg: '', label: 'User Input' },
  pattern: { border: 'border-l-blue-400', bg: 'bg-blue-50', label: 'Pattern Match' },
  rules_engine: { border: 'border-l-purple-400', bg: 'bg-purple-50', label: 'Rules Engine' },
  code_requirement: { border: 'border-l-purple-400', bg: 'bg-purple-50', label: 'Code Requirement' },
  ai_suggestion: { border: 'border-l-amber-400', bg: 'bg-amber-50', label: 'AI Suggestion' },
  companion: { border: 'border-l-teal-400', bg: 'bg-teal-50', label: 'Companion Item' },
};

const DEFAULT_SOURCE_STYLE = { border: 'border-l-gray-200', bg: '', label: 'Unknown' };

// ================================================================= //
//          Provenance Configuration (B-171)                           //
// ================================================================= //

/**
 * Per-line provenance display. Every generated line carries a `provenance_tag`
 * from the scope engine (stated / existing_scope / inherited / house_default /
 * assumed / rule / llm_resolved / gap_candidate). We badge each line's origin
 * precisely and tint its left border to match, so an estimator can tell at a
 * glance what was stated vs assumed vs rule-injected.
 *
 * `assumption` marks the inferred origins the estimator can override by adding
 * a correcting note and regenerating (engine precedence: stated > assumed).
 */
interface ProvenanceStyle {
  label: string;
  chip: string;
  border: string;
  assumption?: boolean;
}

const PROVENANCE_STYLES: Record<string, ProvenanceStyle> = {
  stated: {
    label: 'Stated',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    border: 'border-l-emerald-400',
  },
  existing_scope: {
    label: 'Existing',
    chip: 'bg-slate-100 text-slate-600 border-slate-200',
    border: 'border-l-slate-300',
  },
  inherited: {
    label: 'Inherited',
    chip: 'bg-blue-50 text-blue-700 border-blue-200',
    border: 'border-l-blue-400',
  },
  house_default: {
    label: 'House default',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    border: 'border-l-indigo-400',
    assumption: true,
  },
  assumed: {
    label: 'Assumed',
    chip: 'bg-amber-50 text-amber-800 border-amber-200',
    border: 'border-l-amber-400',
    assumption: true,
  },
  rule: {
    label: 'Rule',
    chip: 'bg-purple-50 text-purple-700 border-purple-200',
    border: 'border-l-purple-400',
  },
  llm_resolved: {
    label: 'AI-resolved',
    chip: 'bg-teal-50 text-teal-700 border-teal-200',
    border: 'border-l-teal-400',
  },
  gap_candidate: {
    label: 'Suggestion',
    chip: 'bg-amber-50 text-amber-800 border-amber-200',
    border: 'border-l-amber-400',
    assumption: true,
  },
};

// Fallback for legacy (non-engine) items that carry only `source`, no tag.
const SOURCE_TO_PROVENANCE: Record<string, string> = {
  user_input: 'stated',
  rule_injected: 'rule',
  rules_engine: 'rule',
  code_requirement: 'rule',
  retrieval: 'llm_resolved',
  ai_suggestion: 'assumed',
  pattern: 'inherited',
  companion: 'inherited',
};

const DEFAULT_PROVENANCE: ProvenanceStyle = {
  label: 'Unknown',
  chip: 'bg-gray-100 text-gray-600 border-gray-200',
  border: 'border-l-gray-200',
};

/**
 * Resolve a line's provenance display, tag-first with a `source` fallback so
 * legacy items still get a sensible chip.
 */
function resolveProvenance(item: { provenance_tag?: string | null; source?: string }): ProvenanceStyle {
  const tag = item.provenance_tag || SOURCE_TO_PROVENANCE[item.source ?? ''] || '';
  return PROVENANCE_STYLES[tag] ?? DEFAULT_PROVENANCE;
}

// ================================================================= //
//          Activity Symbol Map (RULE-008)                             //
// ================================================================= //

/**
 * Maps activity strings to their single-character action symbols per RULE-008.
 * Lookup is case-insensitive. Unrecognised values fall back to the original
 * string truncated to the column width.
 */
const ACTIVITY_SYMBOLS: Record<string, string> = {
  'replace': '+',
  'remove': '-',
  'remove and replace': '&',
  'remove & replace': '&',
  'detach and reset': 'R',
  'detach & reset': 'R',
  'material only': 'M',
  'install only': 'I',
};

function resolveActivitySymbol(activity: string): { symbol: string; label: string } {
  const key = (activity || '').trim().toLowerCase();
  const symbol = ACTIVITY_SYMBOLS[key];
  if (symbol) return { symbol, label: activity };
  // Reason: Unknown activity strings (legacy data, abbreviations like "RC")
  // still need to render without breaking the row; show the raw value.
  return { symbol: activity || '—', label: activity || 'Unknown' };
}

// ================================================================= //
//          ScopeLineItem Component                                    //
// ================================================================= //

interface ScopeLineItemProps {
  item: ScopeResponseLineItem;
  showProvenance?: boolean;
  showSources?: boolean;
  /**
   * Compact list-mode rendering per RULE-017. When true, the note line is
   * hidden by default and the whole row is a click target that toggles the
   * expanded provenance/reasoning panel. When false (card view), the note
   * stays inline and only the info button toggles the panel.
   */
  compact?: boolean;
  /** Dev-mode: show the pipeline trace chevron when item.debug is present. */
  showDebug?: boolean;
  /** Dev-mode: reveal debug sections one click at a time. */
  stepMode?: boolean;
}

/**
 * Renders a single scope line item with source color coding,
 * confidence badge, provenance popover, and copy button.
 *
 * Read-only display per user decision -- no inline editing.
 *
 * Column order (RULE-008):
 *   [code] [activity symbol] [amount/variable] [description] [confidence] [info] [copy]
 */
export const ScopeLineItem: React.FC<ScopeLineItemProps> = ({
  item,
  showProvenance = false,
  showSources = false,
  compact = false,
  showDebug = false,
  stepMode = false,
}) => {
  const [showInfo, setShowInfo] = useState(showProvenance);
  const [showDebugDrawer, setShowDebugDrawer] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasDebug = showDebug && Boolean(item.debug);

  const sourceStyle = SOURCE_STYLES[item.source] || DEFAULT_SOURCE_STYLE;
  const prov = resolveProvenance(item);
  const { symbol: activitySymbol, label: activityLabel } = resolveActivitySymbol(item.activity);
  // Reason: RULE-019 — reasoning is primary; note is the day-one fallback until
  // the retrieval refactor populates reasoning on every item.
  const reasoning = item.reasoning ?? item.note ?? null;

  /**
   * Copies the item note to clipboard (notes only, not description or codes).
   * Shows a brief "Copied!" feedback by swapping the icon.
   * Button is disabled when no note exists.
   */
  const hasNote = Boolean(item.note);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.note) return;
    try {
      await navigator.clipboard.writeText(item.note);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Reason: Fallback for environments where clipboard API is unavailable
    }
  };

  const handleInfoToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowInfo((prev) => !prev);
  };

  const handleRowClick = () => {
    if (!compact) return;
    setShowInfo((prev) => !prev);
  };

  // Render the amount column: variable if present, else quantity + unit, else em-dash.
  const renderAmount = () => {
    if (item.variable) {
      return (
        <span
          className="font-mono text-sm whitespace-nowrap shrink-0 w-20 text-right"
          title={`Variable: ${item.variable}`}
        >
          {item.variable}
        </span>
      );
    }
    return (
      <span className="font-mono text-sm whitespace-nowrap shrink-0 w-20 text-right">
        {item.quantity ?? '—'}{' '}
        <span className="text-xs text-muted-foreground">{item.unit}</span>
      </span>
    );
  };

  return (
    <div
      className={cn(
        'border-l-4 rounded-md px-3 py-2 flex flex-col gap-1 transition-colors',
        // B-171: tint the left border by provenance (tag-first) so origin is
        // legible at a glance; keep the subtle source background.
        prov.border,
        sourceStyle.bg || 'bg-white',
        compact && 'cursor-pointer hover:bg-gray-50'
      )}
      onClick={handleRowClick}
      role={compact ? 'button' : undefined}
      tabIndex={compact ? 0 : undefined}
      onKeyDown={
        compact
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowInfo((prev) => !prev);
              }
            }
          : undefined
      }
    >
      {/* Main row: code, activity symbol, amount/variable, description, confidence, actions */}
      <div className="flex items-center gap-3 min-h-[2rem]">
        {/* Xactimate code */}
        <span className="font-mono text-xs text-primary whitespace-nowrap w-24 shrink-0">
          {item.xactimate_code}
        </span>

        {/* Activity symbol (RULE-008) — single char, always visible, tooltip shows full text */}
        <span
          className="font-mono text-sm font-semibold text-muted-foreground text-center w-6 shrink-0"
          title={activityLabel}
          aria-label={`Activity: ${activityLabel}`}
        >
          {activitySymbol}
        </span>

        {/* Amount or sketch variable */}
        {renderAmount()}

        {/* Description + provenance badge */}
        <span className="text-sm text-foreground flex-grow min-w-0 flex items-start gap-1.5">
          <span className="truncate">{item.description}</span>
          {item.provenance && (
            <ProvenanceBadge provenance={item.provenance} showSources={showSources} />
          )}
        </span>

        {/* Provenance chip (B-171) — every line shows where it came from */}
        <span
          className={cn(
            'shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-none',
            prov.chip
          )}
          title={
            prov.assumption
              ? `${prov.label} — inferred, not stated. Add a note with the actual value and regenerate to override.`
              : `Provenance: ${prov.label}`
          }
          aria-label={`Provenance: ${prov.label}`}
        >
          {prov.label}
        </span>

        {/* Confidence badge */}
        <div className="shrink-0">
          <ConfidenceBadge confidence={item.confidence} />
        </div>

        {/* Info toggle */}
        <button
          onClick={handleInfoToggle}
          className="shrink-0 p-1 rounded hover:bg-gray-100 text-muted-foreground hover:text-foreground transition-colors"
          title="Show details"
          aria-label="Show details"
        >
          <Info className="h-3.5 w-3.5" />
        </button>

        {/* Debug trace toggle — dev mode only */}
        {hasDebug && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDebugDrawer((v) => !v);
            }}
            className="shrink-0 p-1 rounded hover:bg-purple-100 text-purple-700 transition-colors"
            title={showDebugDrawer ? 'Hide pipeline trace' : 'Show pipeline trace'}
            aria-label={showDebugDrawer ? 'Hide pipeline trace' : 'Show pipeline trace'}
            aria-expanded={showDebugDrawer}
          >
            {showDebugDrawer ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        )}

        {/* Copy button -- copies note only */}
        <button
          onClick={handleCopy}
          disabled={!hasNote}
          className={cn(
            'shrink-0 p-1 rounded transition-colors',
            hasNote
              ? 'hover:bg-gray-100 text-muted-foreground hover:text-foreground cursor-pointer'
              : 'text-gray-300 cursor-not-allowed'
          )}
          title={hasNote ? 'Copy note' : 'No notes to copy'}
          aria-label={hasNote ? 'Copy note' : 'No notes to copy'}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-600" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {/* Note line — inline in card view, hidden in compact list view until expanded */}
      {!compact && reasoning && (
        <p className="text-xs text-muted-foreground pl-[10rem]">{reasoning}</p>
      )}

      {/* Helpful Mode annotation — small inline block when helpful_mode
          was on for this request. PRP-009 §3.2: shows alternatives the
          matcher considered, the rule that fired, and a "why this code"
          one-liner without exposing full LLM prompts/responses. */}
      {item.helpful_annotation && (
        <HelpfulAnnotation annotation={item.helpful_annotation} />
      )}

      {/* Debug pipeline trace drawer (dev mode) */}
      {hasDebug && showDebugDrawer && item.debug && (
        <ScopeDebugDrawer debug={item.debug} stepMode={stepMode} />
      )}

      {/* Expanded details panel — reasoning first, then secondary metadata */}
      {showInfo && (
        <div className="mt-1 ml-[10rem] rounded border bg-white p-2 text-xs space-y-2 shadow-sm">
          {/* RULE-019: reasoning is primary content, not decorative */}
          {reasoning && (
            <div>
              <p className="font-medium text-foreground">Why this item:</p>
              <p className="text-foreground">{reasoning}</p>
            </div>
          )}
          {/* Secondary metadata (provenance, confidence, full activity name) */}
          <div className="text-muted-foreground space-y-0.5">
            <p>
              <span className="font-medium">Provenance:</span> {prov.label}
            </p>
            <p>
              <span className="font-medium">Action:</span> {activityLabel}
            </p>
            <p>
              <span className="font-medium">Confidence:</span>{' '}
              <span className="capitalize">{item.confidence}</span>
            </p>
          </div>
          {/* B-171: assumptions are overridable via the room note + regenerate.
              The scope lines themselves stay read-only (prior product decision);
              the engine's provenance precedence (stated > assumed) replaces the
              inference when the estimator states the real value. */}
          {prov.assumption && (
            <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
              <span className="font-medium">Override this assumption:</span> this
              value was inferred, not stated. Add a note for this room with the
              actual value (e.g. &ldquo;walls are plaster&rdquo;) and regenerate —
              a stated value always wins over an assumption.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ================================================================= //
//          HelpfulAnnotation — inline "why this code" summary         //
// ================================================================= //

/**
 * Renders the compact helpful-mode annotation below a line item. Designed
 * to be unobtrusive (small text, amber accent) so it doesn't compete with
 * the line item itself. PRP-009 §3.2.
 */
const HelpfulAnnotation: React.FC<{ annotation: ScopeHelpfulAnnotation }> = ({
  annotation,
}) => {
  const { rule, alternatives, why } = annotation;
  const hasContent = Boolean(rule || (alternatives && alternatives.length > 0) || why);
  if (!hasContent) return null;

  return (
    <div className="ml-[10rem] mt-1 rounded border border-amber-200 bg-amber-50/60 px-2 py-1.5 text-[11px] text-amber-900">
      {rule && (
        <div>
          <span className="font-medium">Rule:</span>{' '}
          <span className="font-mono">{rule.reference}</span>
          {rule.summary && <span className="text-amber-800"> · {rule.summary}</span>}
        </div>
      )}
      {why && !rule && (
        <div>
          <span className="font-medium">Why:</span> {why}
        </div>
      )}
      {alternatives && alternatives.length > 0 && (
        <div className="mt-0.5">
          <span className="font-medium">Alternatives considered:</span>{' '}
          {alternatives.map((alt, i) => (
            <span key={alt.code} className="ml-1">
              <span className="font-mono">{alt.code}</span>
              {typeof alt.score === 'number' && (
                <span className="text-amber-700"> ({alt.score.toFixed(2)})</span>
              )}
              {i < alternatives.length - 1 ? ',' : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
